import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLocalizedUrl } from '../utils/urlHelper';
import SeoMeta from '../components/SeoMeta';
import ContactChannels from '../components/common/ContactChannels';
import ContactRequestForm from '../components/common/ContactRequestForm';
import { PageBand } from '../components/common/B2B';

const TEXTS = {
  es: {
    title: 'Le llamamos',
    subtitle: 'Déjenos su teléfono y le llamamos en horario de atención.',
    back: 'Volver',
    seoDescription: 'Déjenos su teléfono y el equipo de BlueCMR le llama en horario de atención.',
  },
  en: {
    title: 'We call you',
    subtitle: 'Leave your phone number and we will call you during business hours.',
    back: 'Back',
    seoDescription: 'Leave your phone number and the BlueCMR team will call you during business hours.',
  },
};

/**
 * Vía «que me llamen» de /contacto: mismo envío que el formulario, con teléfono obligatorio.
 * noindex: indexada, Google la sacaba en búsquedas genéricas («quiero que me
 * llamen») y llegaban peticiones de gente que no sabía qué es BlueCMR (oct-2026).
 *
 * Solo se llega navegando dentro de la web: si es la página de entrada (Google,
 * enlace externo, URL tecleada) se manda a /contacto. Mientras Google la seguía
 * mostrando, alguien la usaba para apuntar a terceros con nombre y teléfono
 * reales y que les llamásemos sin haberlo pedido (28/29-oct-2026).
 */
const CallbackPage = () => {
  const { i18n } = useTranslation();
  const { getLocalizedUrl } = useLocalizedUrl();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const t = TEXTS[lang];
  const origin = searchParams.get('origin') || '';

  // location.key 'default' = primera entrada del historial de la SPA. El
  // prerender (puppeteer, navigator.webdriver) no redirige: hornea esta página
  // con su noindex.
  const isLandingPage = location.key === 'default' && !navigator.webdriver;
  useEffect(() => {
    if (isLandingPage) navigate(`${getLocalizedUrl('/contacto')}${location.search}`, { replace: true });
  }, [isLandingPage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-white">
      <SeoMeta
        title={`${t.title} — BlueCMR`}
        description={t.seoDescription}
        noindex
        canonical="https://bluecmr.com/contacto/llamada"
        alternates={{
          es: 'https://www.bluecmr.com/contacto/llamada',
          en: 'https://www.bluecmr.com/en/contact/call',
          'x-default': 'https://www.bluecmr.com/contacto/llamada'
        }}
        breadcrumbs={[
          { name: 'Inicio', url: 'https://bluecmr.com/' },
          { name: 'Contacto', url: 'https://bluecmr.com/contacto' },
          { name: 'Le llamamos', url: 'https://bluecmr.com/contacto/llamada' }
        ]}
      />

      <PageBand eyebrow={lang === 'es' ? 'Contacto' : 'Contact'} title={t.title} text={t.subtitle} />

      <section className="py-12">
        <div className="max-w-lg mx-auto px-4">
          <Link
            to={getLocalizedUrl('/contacto')}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>

          <ContactRequestForm kind="callback" lang={lang} origin={origin} />

          <ContactChannels lang={lang} compact className="mt-10" />
        </div>
      </section>
    </div>
  );
};

export default CallbackPage;
